import {Injectable, computed, inject, signal} from '@angular/core';
import {NavigationEnd, Router, ActivatedRouteSnapshot} from '@angular/router';
import {filter} from 'rxjs';
import {LAUNCHER_APPS} from 'src/app/features/launcher/config/launcher.config';
import {AppRoutes} from '../models/routes.model';

@Injectable({
    providedIn: 'root',
})
export class AppContextService {
    private readonly router = inject(Router);
    private readonly appCodeSignal = signal<string | null>(null);

    readonly currentAppCode = computed(() => this.appCodeSignal());
    readonly currentAppName = computed(() => {
        const appCode = this.appCodeSignal();

        if (!appCode) {
            return null;
        }

        return (
            LAUNCHER_APPS.find((app) => app.code === appCode)?.name ??
            this.formatAppName(appCode)
        );
    });

    constructor() {
        this.syncFromCurrentRoute();

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => this.syncFromCurrentRoute());
    }

    buildAppScopedUrl(route: string): string {
        const appCode = this.appCodeSignal();

        // Fuori dalle rotte applicative (pagine di autenticazione, dichiarazione
        // di accessibilità) il codice applicazione non c'è: si ripiega
        // sull'unica applicazione in catalogo, non più sul launcher.
        if (!appCode) {
            return `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${route}`;
        }

        return `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${appCode}/${route}`;
    }

    private syncFromCurrentRoute(): void {
        this.appCodeSignal.set(this.resolveAppCode(this.router.routerState.snapshot.root));
    }

    private resolveAppCode(route: ActivatedRouteSnapshot | null): string | null {
        if (!route) {
            return null;
        }

        const appCode = route.paramMap.get('appCode');
        if (appCode) {
            return appCode;
        }

        for (const child of route.children) {
            const childAppCode = this.resolveAppCode(child);
            if (childAppCode) {
                return childAppCode;
            }
        }

        return null;
    }

    private formatAppName(appCode: string): string {
        return appCode
            .split('-')
            .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
            .join(' ');
    }
}
